import { ChevronLeft, ChevronRight, ChevronsLeft, ChevronsRight } from "lucide-react";
import { cn } from "../lib/cn";

interface PaginationProps {
  currentPage: number; // 0-indexed
  totalRecords: number;
  limit: number;
  loading?: boolean;
  onPageChange: (page: number) => void;
  onLimitChange: (limit: number) => void;
}

export function Pagination({
  currentPage,
  totalRecords,
  limit,
  loading = false,
  onPageChange,
  onLimitChange,
}: PaginationProps) {
  const totalPages = Math.max(1, Math.ceil(totalRecords / limit));
  const startRecord = totalRecords === 0 ? 0 : currentPage * limit + 1;
  const endRecord = Math.min((currentPage + 1) * limit, totalRecords);

  // Generate page numbers to display
  const pages: number[] = [];
  const maxVisiblePages = 5;
  let startPage = Math.max(0, currentPage - 2);
  let endPage = Math.min(totalPages - 1, startPage + maxVisiblePages - 1);

  if (endPage - startPage < maxVisiblePages - 1) {
    startPage = Math.max(0, endPage - maxVisiblePages + 1);
  }

  for (let i = startPage; i <= endPage; i++) {
    pages.push(i);
  }

  const handlePageClick = (page: number) => {
    if (page >= 0 && page < totalPages && page !== currentPage && !loading) {
      onPageChange(page);
    }
  };

  return (
    <div className="flex flex-col sm:flex-row items-center justify-between gap-4 py-3 border-t border-slate-100/80 mt-2 select-none">
      {/* Left side: showing and limit select */}
      <div className="flex items-center gap-3 text-xs font-semibold text-slate-500">
        <span>
          Showing <span className="font-bold text-slate-800">{startRecord}</span> –{" "}
          <span className="font-bold text-slate-800">{endRecord}</span> of{" "}
          <span className="font-bold text-slate-800">{totalRecords}</span>
        </span>
        <span className="text-slate-300">|</span>
        <div className="flex items-center gap-1.5">
          <span>Show</span>
          <select
            value={limit}
            onChange={(e) => onLimitChange(Number(e.target.value))}
            disabled={loading}
            className="rounded-lg border border-slate-250/60 bg-white px-2 py-1 text-xs font-bold text-slate-700 shadow-2xs focus:border-brand-500 focus:outline-none cursor-pointer disabled:opacity-50"
          >
            <option value={10}>10</option>
            <option value={25}>25</option>
            <option value={50}>50</option>
            <option value={100}>100</option>
          </select>
          <span>records</span>
        </div>
      </div>

      {/* Right side: navigation buttons */}
      <div className="flex items-center gap-1">
        {/* First page */}
        <button
          onClick={() => handlePageClick(0)}
          disabled={currentPage === 0 || loading}
          className={cn(
            "inline-flex h-8 w-8 items-center justify-center rounded-lg border text-slate-600 transition-all cursor-pointer",
            currentPage === 0
              ? "bg-slate-50/50 border-slate-200/50 text-slate-300 cursor-not-allowed"
              : "bg-white border-slate-250/60 hover:bg-slate-50 active:scale-95"
          )}
          title="First Page"
        >
          <ChevronsLeft className="h-4 w-4" />
        </button>

        {/* Previous page */}
        <button
          onClick={() => handlePageClick(currentPage - 1)}
          disabled={currentPage === 0 || loading}
          className={cn(
            "inline-flex h-8 w-8 items-center justify-center rounded-lg border text-slate-600 transition-all cursor-pointer",
            currentPage === 0
              ? "bg-slate-50/50 border-slate-200/50 text-slate-300 cursor-not-allowed"
              : "bg-white border-slate-250/60 hover:bg-slate-50 active:scale-95"
          )}
          title="Previous Page"
        >
          <ChevronLeft className="h-4 w-4" />
        </button>

        {/* Page numbers */}
        {pages.map((p) => {
          const isActive = p === currentPage;
          return (
            <button
              key={p}
              onClick={() => handlePageClick(p)}
              disabled={loading}
              className={cn(
                "inline-flex h-8 min-w-8 items-center justify-center rounded-lg border text-xs font-bold transition-all cursor-pointer px-1.5",
                isActive
                  ? "bg-brand-600 border-brand-600 text-white shadow-2xs hover:bg-brand-700"
                  : "bg-white border-slate-250/60 text-slate-700 hover:bg-slate-50 active:scale-95"
              )}
            >
              {p + 1}
            </button>
          );
        })}

        {/* Next page */}
        <button
          onClick={() => handlePageClick(currentPage + 1)}
          disabled={currentPage === totalPages - 1 || loading}
          className={cn(
            "inline-flex h-8 w-8 items-center justify-center rounded-lg border text-slate-600 transition-all cursor-pointer",
            currentPage === totalPages - 1
              ? "bg-slate-50/50 border-slate-200/50 text-slate-300 cursor-not-allowed"
              : "bg-white border-slate-250/60 hover:bg-slate-50 active:scale-95"
          )}
          title="Next Page"
        >
          <ChevronRight className="h-4 w-4" />
        </button>

        {/* Last page */}
        <button
          onClick={() => handlePageClick(totalPages - 1)}
          disabled={currentPage === totalPages - 1 || loading}
          className={cn(
            "inline-flex h-8 w-8 items-center justify-center rounded-lg border text-slate-600 transition-all cursor-pointer",
            currentPage === totalPages - 1
              ? "bg-slate-50/50 border-slate-200/50 text-slate-300 cursor-not-allowed"
              : "bg-white border-slate-250/60 hover:bg-slate-50 active:scale-95"
          )}
          title="Last Page"
        >
          <ChevronsRight className="h-4 w-4" />
        </button>
      </div>
    </div>
  );
}
